import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  ArrowRight, 
  Bot, 
  MessageSquare, 
  Users, 
  Target, 
  Zap, 
  Clock, 
  TrendingUp,
  CheckCircle2,
  Star,
  Globe,
  Search,
  Heart,
  UserPlus,
  BarChart3,
  Shield,
  Sparkles,
  MessageCircle,
  Send,
  Rocket,
  Brain,
  AlertCircle,
  Mail,
  Eye,
  UserCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const XAgentsPage = () => {
  // Scroll to top IMMEDIATELY when component starts (before any state or effects)
  if (typeof window !== 'undefined') {
    window.scrollTo(0, 0);
  }

  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0
  });
  const [isInterested, setIsInterested] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  // Force scroll to top when component mounts - backup for any async issues
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  // Track mouse position for spotlight effect
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: e.clientX,
        y: e.clientY
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Handle mouse movement for spotlight effect
  const handleMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  // Handle beta interest signup
  const handleBetaInterest = async () => {
    if (!user?.email) {
      toast.error("Please sign in to join the beta");
      return;
    }

    setLoading(true);
    try {
      // Update user_dashboard table with beta interest
      const { error } = await supabase
        .from('user_dashboard')
        .upsert({
          email: user.email,
          x_agent_beta_interested: true,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'email'
        });

      if (error) throw error;

      setIsInterested(true);
      toast.success("Thanks for your interest! We'll notify you when X-Agent is ready.");
    } catch (error) {
      console.error('Error updating beta interest:', error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const fadeInLeft = {
    hidden: { opacity: 0, x: -30 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.6 } }
  };

  const fadeInRight = {
    hidden: { opacity: 0, x: 30 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#fafafa] dark:bg-gray-950">
      {/* Hero Section */}
      <section className="relative py-12 md:py-16 overflow-hidden">
        {/* Background elements */}
        <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:20px_20px] opacity-30 dark:bg-[radial-gradient(#1f2937_1px,transparent_1px)]"></div>
        
        <motion.div 
          className="absolute top-1/4 right-[15%] w-96 h-96 rounded-full bg-blue-400/5 dark:bg-blue-400/10 blur-[100px]"
          animate={{ 
            y: [0, 30, 0],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ 
            duration: 12, 
            repeat: Infinity,
            ease: "easeInOut" 
          }}
        />
        
        <motion.div 
          className="absolute bottom-1/4 left-[10%] w-80 h-80 rounded-full bg-blue-400/5 dark:bg-blue-400/10 blur-[100px]"
          animate={{ 
            y: [0, -40, 0],
            opacity: [0.4, 0.6, 0.4],
          }}
          transition={{ 
            duration: 15, 
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />
        
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div 
            className="max-w-5xl mx-auto text-center"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            {/* Badge */}
            <motion.div 
              className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm font-medium mb-6"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Rocket className="w-4 h-4 mr-2" />
              Coming Soon - X DM Automation Agent
            </motion.div>
            
            {/* Main headline */}
            <motion.h1 
              className="text-4xl md:text-6xl font-bold tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-400" 
              variants={fadeInUp}
            >
              <span className="gradient-text">
                X Agent
              </span>
              <br />
              Coming Soon
            </motion.h1>
            
            {/* Subheadline */}
            <motion.p 
              className="text-xl text-gray-600 dark:text-gray-300 mb-10 max-w-4xl mx-auto leading-relaxed" 
              variants={fadeInUp}
            >
              Our X Agent will automatically find your ideal customers on X.com, craft personalized DMs that feel human, 
              and intelligently follow up to convert prospects into paying customers—all while you focus on building your product.
            </motion.p>

            {/* Beta Message */}
            <motion.div 
              className="bg-gradient-to-r from-blue-50 to-blue-50 dark:from-blue-900/20 dark:to-blue-900/20 border border-blue-200 dark:border-blue-800/30 rounded-2xl p-8 mb-10 mx-auto max-w-2xl"
              variants={fadeInUp}
            >
              <div className="flex items-center justify-center mb-4">
                <AlertCircle className="w-6 h-6 text-blue-600 dark:text-blue-400 mr-2" />
                <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100">Beta Testing Phase</h3>
              </div>
              <p className="text-blue-700 dark:text-blue-300 mb-6">
                We're currently performing beta tests with select users to perfect the X Agent DM automation experience. 
                Join our waitlist to be among the first to access this powerful customer acquisition tool.
              </p>
              
              {/* CTA Button */}
              <motion.div variants={fadeInUp}>
                <Button 
                  size="lg" 
                  onClick={handleBetaInterest}
                  disabled={loading || isInterested}
                  className="relative overflow-hidden group bg-primary hover:bg-primary/90 text-primary-foreground px-10 py-6 text-lg font-semibold rounded-xl shadow-2xl border-0"
                  onMouseMove={handleMouseMove}
                >
                  <span className="relative z-10 flex items-center">
                    {isInterested ? (
                      <>
                        <CheckCircle2 className="mr-2 h-5 w-5" />
                        signed up
                      </>
                    ) : loading ? (
                      "Joining..."
                    ) : (
                      <>
                        I'm Interested in Beta Testing
                        <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                      </>
                    )}
                  </span>
                  <span className="spotlight" style={{ 
                    '--x': `${mousePosition.x}px`,
                    '--y': `${mousePosition.y}px`,
                  } as React.CSSProperties}></span>
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Features Preview Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white dark:from-gray-950/50 dark:to-gray-950 relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:20px_20px] opacity-30 dark:bg-[radial-gradient(#1f2937_1px,transparent_1px)]"></div>
        
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="max-w-6xl mx-auto">
            <motion.div 
              className="text-center mb-16"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={staggerContainer}
            >
              <motion.h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6" variants={fadeInUp}>
                What X Agent Will Do For You
              </motion.h2>
              <motion.p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto" variants={fadeInUp}>
                Autonomous DM automation that finds your customers and converts them through personalized outreach.
              </motion.p>
            </motion.div>
            
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Feature Card 1 - Smart Audience Discovery */}
              <motion.div
                className="group relative bg-white dark:bg-gray-900 rounded-2xl p-8 border border-gray-100 dark:border-gray-800 shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInLeft}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-blue-50/50 dark:from-blue-900/10 dark:to-blue-900/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="relative z-10">
                  <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-2xl w-fit mb-6">
                    <Eye className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Smart Audience Discovery</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-6">
                    AI-powered identification of your ideal customers on X.com based on interests, behavior, and engagement patterns perfect for outreach.
                  </p>
                  <div className="flex items-center space-x-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Advanced prospect targeting</span>
                  </div>
                </div>
              </motion.div>
              
              {/* Feature Card 2 - Personalized Outreach */}
              <motion.div
                className="group relative bg-white dark:bg-gray-900 rounded-2xl p-8 border border-gray-100 dark:border-gray-800 shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInUp}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-cyan-50/50 dark:from-blue-900/10 dark:to-cyan-900/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="relative z-10">
                  <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-2xl w-fit mb-6">
                    <Mail className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Personalized Outreach</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-6">
                    Crafts authentic, personalized DMs that feel human and convert. Each message is tailored to the recipient's interests and behavior.
                  </p>
                  <div className="flex items-center space-x-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Human-like messaging</span>
                  </div>
                </div>
              </motion.div>
              
              {/* Feature Card 3 - Smart Follow-up & Analytics */}
              <motion.div
                className="group relative bg-white dark:bg-gray-900 rounded-2xl p-8 border border-gray-100 dark:border-gray-800 shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInRight}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-50/50 to-blue-50/50 dark:from-cyan-900/10 dark:to-blue-900/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="relative z-10">
                  <div className="p-3 bg-cyan-100 dark:bg-cyan-900/30 rounded-2xl w-fit mb-6">
                    <BarChart3 className="w-8 h-8 text-cyan-600 dark:text-cyan-400" />
                  </div>
                  <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Smart Follow-up & Analytics</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-6">
                    Intelligently follows up based on responses and tracks conversion metrics to optimize your outreach campaigns continuously.
                  </p>
                  <div className="flex items-center space-x-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Conversion tracking</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 bg-gradient-to-b from-white to-gray-50 dark:from-gray-950 dark:to-gray-900/50 relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="max-w-6xl mx-auto">
            <motion.div 
              className="text-center mb-16"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={staggerContainer}
            >
              <motion.h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6" variants={fadeInUp}>
                How X Agent Will Work
              </motion.h2>
              <motion.p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto" variants={fadeInUp}>
                Set it up once, and let our AI handle your entire X.com outreach and conversion strategy.
              </motion.p>
            </motion.div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Step 1 */}
              <motion.div
                className="text-center group"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInUp}
              >
                <div className="bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/30 dark:to-blue-800/30 rounded-2xl p-6 mb-6 group-hover:scale-105 transition-transform duration-300">
                  <Bot className="w-12 h-12 text-blue-600 dark:text-blue-400 mx-auto" />
                </div>
                <h3 className="text-lg font-bold mb-3 text-gray-900 dark:text-white">Setup & Connect</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Connect your X.com account and define your ideal customer profile and messaging preferences.
                </p>
              </motion.div>
              
              {/* Step 2 */}
              <motion.div
                className="text-center group"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInUp}
                transition={{ delay: 0.1 }}
              >
                <div className="bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/30 dark:to-blue-800/30 rounded-2xl p-6 mb-6 group-hover:scale-105 transition-transform duration-300">
                  <UserCheck className="w-12 h-12 text-blue-600 dark:text-blue-400 mx-auto" />
                </div>
                <h3 className="text-lg font-bold mb-3 text-gray-900 dark:text-white">Find Prospects</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Our AI analyzes X.com to find users who match your ideal customer profile and are likely to convert.
                </p>
              </motion.div>
              
              {/* Step 3 */}
              <motion.div
                className="text-center group"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInUp}
                transition={{ delay: 0.2 }}
              >
                <div className="bg-gradient-to-br from-cyan-100 to-cyan-200 dark:from-cyan-900/30 dark:to-cyan-800/30 rounded-2xl p-6 mb-6 group-hover:scale-105 transition-transform duration-300">
                  <Send className="w-12 h-12 text-cyan-600 dark:text-cyan-400 mx-auto" />
                </div>
                <h3 className="text-lg font-bold mb-3 text-gray-900 dark:text-white">Send Personal DMs</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Automatically crafts and sends personalized DMs that feel authentic and drive engagement.
                </p>
              </motion.div>
              
              {/* Step 4 */}
              <motion.div
                className="text-center group"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInUp}
                transition={{ delay: 0.3 }}
              >
                <div className="bg-gradient-to-br from-green-100 to-green-200 dark:from-green-900/30 dark:to-green-800/30 rounded-2xl p-6 mb-6 group-hover:scale-105 transition-transform duration-300">
                  <TrendingUp className="w-12 h-12 text-green-600 dark:text-green-400 mx-auto" />
                </div>
                <h3 className="text-lg font-bold mb-3 text-gray-900 dark:text-white">Follow-up & Convert</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Intelligently follows up based on responses and optimizes messaging to maximize conversions.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default XAgentsPage; 